import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AvailabilityType,
  BlackoutReasonType,
  BookableResourceType,
  FilePurposeType,
  MaintenanceAction,
  MaintenanceImpactType,
  PolicyAction,
  PolicyType,
  ResourceAction,
  ResourceDetailResponse,
  ResourceDto,
  ResourcesListParams,
  ResourcesListResponse,
  ScheduleWindowAction,
  VisibilityStatus,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/bookable-resources`;
const SUPPLY = `${API_VERSION_PREFIX}/hq/supply`;

function toQuery(params: ResourcesListParams): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.sort) search.set("sort", params.sort);
  if (params.q) search.set("q", params.q);
  if (params.business_id) search.set("business_id", params.business_id);
  if (params.business_capability_id) {
    search.set("business_capability_id", params.business_capability_id);
  }
  if (params.market_id) search.set("market_id", params.market_id);
  if (params.status) search.set("status", params.status);
  if (params.resource_type) search.set("resource_type", params.resource_type);
  if (params.visibility_status) {
    search.set("visibility_status", params.visibility_status);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createResourcesApi(client: ApiClient) {
  return {
    list(params: ResourcesListParams = {}) {
      return client.get<ResourcesListResponse>(`${BASE}${toQuery(params)}`);
    },

    getDetail(resourceId: string) {
      return client.get<ResourceDetailResponse>(`${BASE}/${resourceId}`);
    },

    create(body: {
      business_capability_id: string;
      name: string;
      resource_type: BookableResourceType;
      capacity?: number | null;
      visibility_status?: VisibilityStatus;
    }) {
      return client.post<{ resource: ResourceDto; meta: ResourcesListResponse["meta"] }>(
        BASE,
        body,
        newIdempotencyKey(),
      );
    },

    update(
      resourceId: string,
      body: {
        name?: string;
        capacity?: number | null;
        visibility_status?: VisibilityStatus;
      },
    ) {
      return client.patch<{ resource: ResourceDto; meta: ResourcesListResponse["meta"] }>(
        `${BASE}/${resourceId}`,
        body,
        newIdempotencyKey(),
      );
    },

    applyAction(resourceId: string, action: ResourceAction, body: { reason: string }) {
      return client.post(
        `${BASE}/${resourceId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    attachFile(
      resourceId: string,
      body: { file_asset_id: string; purpose_type: FilePurposeType },
    ) {
      return client.post(
        `${BASE}/${resourceId}/files/attach`,
        body,
        newIdempotencyKey(),
      );
    },

    createAvailability(body: {
      business_id: string;
      availability_type: AvailabilityType;
      starts_at: string;
      ends_at: string;
      business_capability_id?: string | null;
      bookable_resource_id?: string | null;
      recurrence_rule?: string | null;
    }) {
      return client.post(`${SUPPLY}/availability`, body, newIdempotencyKey());
    },

    applyAvailabilityAction(
      availabilityId: string,
      action: ScheduleWindowAction,
      body: { reason: string },
    ) {
      return client.post(
        `${SUPPLY}/availability/${availabilityId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    createBlackout(body: {
      business_id: string;
      starts_at: string;
      ends_at: string;
      reason_type: BlackoutReasonType;
      business_capability_id?: string | null;
      bookable_resource_id?: string | null;
    }) {
      return client.post(`${SUPPLY}/blackouts`, body, newIdempotencyKey());
    },

    applyBlackoutAction(
      blackoutId: string,
      action: ScheduleWindowAction,
      body: { reason: string },
    ) {
      return client.post(
        `${SUPPLY}/blackouts/${blackoutId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    createMaintenance(body: {
      business_id: string;
      starts_at: string;
      ends_at?: string | null;
      reason?: string | null;
      bookable_resource_id?: string | null;
      business_capability_id?: string | null;
    }) {
      return client.post(`${SUPPLY}/maintenance`, body, newIdempotencyKey());
    },

    applyMaintenanceAction(
      maintenanceId: string,
      action: MaintenanceAction,
      body: { reason?: string; ends_at?: string | null },
    ) {
      return client.post(
        `${SUPPLY}/maintenance/${maintenanceId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    createImpact(
      maintenanceId: string,
      body: { booking_id: string; impact_type: MaintenanceImpactType },
    ) {
      return client.post(
        `${SUPPLY}/maintenance/${maintenanceId}/impacts`,
        body,
        newIdempotencyKey(),
      );
    },

    createPolicy(body: {
      business_id: string;
      policy_type: PolicyType;
      bookable_resource_id?: string | null;
      business_capability_id?: string | null;
      is_exception?: boolean;
      approval_id?: string;
    }) {
      return client.post(`${SUPPLY}/policies`, body, newIdempotencyKey());
    },

    applyPolicyAction(
      policyId: string,
      action: PolicyAction,
      body: {
        reason: string;
        is_exception?: boolean;
        is_high_impact?: boolean;
        approval_id?: string;
      },
    ) {
      return client.post(
        `${SUPPLY}/policies/${policyId}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type ResourcesApi = ReturnType<typeof createResourcesApi>;
